import React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { DatePicker } from 'antd';
import locale_es from 'antd/es/date-picker/locale/es_ES';
import locale_en from 'antd/es/date-picker/locale/en_US';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';
import dayjs from 'dayjs';

const TableFilterDropdown = ({
  type = 'checkbox', // 'checkbox' or 'dateRange'
  values = [],
  selectedKeys = [],
  setSelectedKeys,
  confirm,
  clearFilters,
  columnName,
  loading = false,
  dateFilterMode,
  onDateFilterModeChange
}) => {
  const { t, i18n } = useTranslation();
  const antdLocale = i18n.language === 'en' ? locale_en : locale_es;
  const [searchTerm, setSearchTerm] = React.useState('');
  // Internal state to prevent parent re-renders on each selection
  const [internalSelectedKeys, setInternalSelectedKeys] = React.useState(selectedKeys);
  const [internalDateMode, setInternalDateMode] = React.useState(dateFilterMode || 'overlap');
  const { RangePicker } = DatePicker;

  // Update internal state when external selectedKeys change (e.g., when dropdown opens)
  React.useEffect(() => {
    setInternalSelectedKeys(selectedKeys);
  }, [selectedKeys]);

  React.useEffect(() => {
    setInternalDateMode(dateFilterMode || 'overlap');
  }, [dateFilterMode]);

  // Filter values based on search
  const filteredValues = React.useMemo(() => {
    if (type === 'dateRange' || !searchTerm) return values;
    const term = searchTerm.toLowerCase();
    return values.filter(value => 
      value.text.toString().toLowerCase().includes(term)
    );
  }, [values, searchTerm, type]);

  const handleSelectAll = () => {
    if (type === 'checkbox') {
      setInternalSelectedKeys(filteredValues.map(v => v.value));
    }
  };

  const handleClearAll = () => {
    setInternalSelectedKeys([]);
  };

  const isAllSelected = type === 'checkbox' && 
    filteredValues.length > 0 && 
    filteredValues.every(v => internalSelectedKeys.includes(v.value));

  return (
    <Box sx={{ 
      width: 300,
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography sx={{ 
          fontWeight: 600, 
          fontSize: '14px',
          color: colors.text.primary,
          mb: 1
        }}>
          {t('filterBy')} {columnName}
        </Typography>
        
        {/* Search field for checkbox filters */}
        {type === 'checkbox' && values.length > 10 && (
          <TextField
            size="small"
            fullWidth
            placeholder={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: colors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              mb: 1,
              '& .MuiOutlinedInput-root': {
                fontSize: '13px',
              }
            }}
          />
        )}
        
        {/* Select/Clear all buttons for checkbox filters */}
        {type === 'checkbox' && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button
              size="small"
              onClick={handleSelectAll}
              disabled={isAllSelected}
              sx={{ 
                textTransform: 'none',
                fontSize: '12px',
                py: 0.5,
                color: colors.text.secondary,
                '&:hover': {
                  backgroundColor: colors.background.subtle
                }
              }}
            >
              {t('selectAll')}
            </Button>
            <Button
              size="small"
              onClick={handleClearAll}
              disabled={internalSelectedKeys.length === 0}
              sx={{ 
                textTransform: 'none',
                fontSize: '12px',
                py: 0.5,
                color: colors.text.secondary,
                '&:hover': {
                  backgroundColor: colors.background.subtle
                }
              }}
            >
              {t('clearAll')}
            </Button>
          </Box>
        )}
      </Box>

      <Divider />

      {/* Content */}
      <Box sx={{ 
        maxHeight: 300,
        overflowY: 'auto',
        p: type === 'dateRange' ? 2 : 0
      }}>
        {type === 'checkbox' ? (
          // Checkbox list
          loading && values.length === 0 ? (
            <Typography sx={{ 
              color: colors.text.secondary,
              fontSize: '13px',
              textAlign: 'center',
              py: 2
            }}>
              {t('loading')}...
            </Typography>
          ) : filteredValues.length === 0 ? (
            <Typography sx={{ 
              color: colors.text.secondary,
              fontSize: '13px',
              textAlign: 'center',
              py: 2
            }}>
              {searchTerm ? t('noMatchesFound') : t('noValuesAvailable')}
            </Typography>
          ) : (
            <Box sx={{ px: 2, py: 1 }}>
              {filteredValues.map((item) => (
                <FormControlLabel
                  key={item.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={internalSelectedKeys.includes(item.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setInternalSelectedKeys([...internalSelectedKeys, item.value]);
                        } else {
                          setInternalSelectedKeys(internalSelectedKeys.filter(key => key !== item.value));
                        }
                      }}
                      sx={{ py: 0.5 }}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontSize: '13px',
                      color: colors.text.primary
                    }}>
                      {item.text}
                    </Typography>
                  }
                  sx={{ 
                    display: 'flex',
                    width: '100%',
                    mx: 0,
                    '&:hover': {
                      backgroundColor: colors.background.subtle
                    }
                  }}
                />
              ))}
            </Box>
          )
        ) : (
          // Date filter: range picker or month picker based on mode
          <>
          {onDateFilterModeChange && (
            <Box sx={{ mb: 1.5, display: 'flex', gap: 0.5 }}>
              <Chip
                label={t('experiments_filterByOverlap')}
                size="small"
                variant={internalDateMode !== 'startDate' ? 'filled' : 'outlined'}
                color={internalDateMode !== 'startDate' ? 'primary' : 'default'}
                onClick={() => setInternalDateMode('overlap')}
                sx={{ fontSize: '11px', height: 24 }}
              />
              <Chip
                label={t('experiments_filterByStartDate')}
                size="small"
                variant={internalDateMode === 'startDate' ? 'filled' : 'outlined'}
                color={internalDateMode === 'startDate' ? 'primary' : 'default'}
                onClick={() => setInternalDateMode('startDate')}
                sx={{ fontSize: '11px', height: 24 }}
              />
            </Box>
          )}
          {internalDateMode === 'startDate' ? (
            <DatePicker
              picker="month"
              locale={antdLocale}
              value={(() => {
                if (!internalSelectedKeys || internalSelectedKeys.length === 0) return null;
                const dateStr = Array.isArray(internalSelectedKeys[0]) ? internalSelectedKeys[0][0] : internalSelectedKeys[0];
                const parsed = dayjs(dateStr, 'DD/MM/YYYY');
                return parsed.isValid() ? parsed : null;
              })()}
              onChange={(date) => {
                if (date) {
                  const startOfMonth = date.startOf('month').format('DD/MM/YYYY');
                  const endOfMonth = date.endOf('month').format('DD/MM/YYYY');
                  setInternalSelectedKeys([startOfMonth, endOfMonth]);
                } else {
                  setInternalSelectedKeys([]);
                }
              }}
              style={{ width: '100%' }}
              format="MM/YYYY"
              allowClear={true}
            />
          ) : (
            <RangePicker
              locale={antdLocale}
              value={(() => {
                if (!internalSelectedKeys || internalSelectedKeys.length === 0) return null;
                const dateRange = Array.isArray(internalSelectedKeys[0]) ? internalSelectedKeys[0] : internalSelectedKeys;
                if (!dateRange || dateRange.length !== 2) return null;

                const startDate = dayjs(dateRange[0], 'DD/MM/YYYY');
                const endDate = dayjs(dateRange[1], 'DD/MM/YYYY');

                return startDate.isValid() && endDate.isValid() ? [startDate, endDate] : null;
              })()}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setInternalSelectedKeys([dates[0].format('DD/MM/YYYY'), dates[1].format('DD/MM/YYYY')]);
                } else {
                  setInternalSelectedKeys([]);
                }
              }}
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              allowClear={true}
              inputReadOnly={true}
            />
          )}
          </>
        )}
      </Box>

      <Divider />

      {/* Footer */}
      <Box sx={{ 
        p: 2, 
        pt: 1.5,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1
      }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setInternalSelectedKeys([]);
            clearFilters && clearFilters();
            setSearchTerm('');
          }}
          disabled={loading}
          sx={{ 
            textTransform: 'none',
            fontSize: '13px',
            borderColor: colors.border.medium,
            color: colors.text.secondary,
            '&:hover': {
              borderColor: colors.border.dark,
              backgroundColor: colors.background.subtle
            }
          }}
        >
          {t('reset')}
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            // Apply date filter mode change if it changed
            if (onDateFilterModeChange && internalDateMode !== dateFilterMode) {
              onDateFilterModeChange(internalDateMode);
            }
            // For card view: pass internal selections to confirm function
            // For table view: call confirm without parameters (Ant Design expects this)
            if (confirm.length > 0) {
              confirm(internalSelectedKeys);
            } else {
              setSelectedKeys(internalSelectedKeys);
              confirm();
            }
            setSearchTerm('');
          }}
          disabled={loading}
          sx={{ 
            textTransform: 'none',
            fontSize: '13px',
            backgroundColor: colors.action.primary,
            '&:hover': {
              backgroundColor: colors.action.primaryHover
            }
          }}
        >
          {t('apply')}
        </Button>
      </Box>
    </Box>
  );
};

export default TableFilterDropdown;